import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HiOutlineCheckCircle, HiOutlineXMark } from 'react-icons/hi2';
import {
  authService,
  rcaService,
  machineService,
  plantService,
  masterDataService,
} from '../services';
import { usePermissions } from '../hooks/usePermissions';
import PageLoading from '../components/common/PageLoading';
import './RCAEntry.css';

const RCAEntry = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const tenantId = authService.getTenantId();
  const currentUser = authService.getUser();
  const { hasPermission } = usePermissions();
  const isEditMode = !!id;

  // Permission checks
  const canCreate = hasPermission('RCA.Create');
  const canEdit = hasPermission('RCA.Edit');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [plants, setPlants] = useState([]);
  const [machines, setMachines] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [formData, setFormData] = useState({
    plantId: '',
    machineId: '',
    triggerReasonId: '',
    triggerReason: '',
    problemDesc: '',
    rootCause: '',
    method: '5 Whys',
    statusId: '',
  });

  const [fiveWhys, setFiveWhys] = useState([
    '',
    '',
    '',
    '',
    '',
  ]);

  const [fishbone, setFishbone] = useState({
    man: '',
    machine: '',
    method: '',
    material: '',
    environment: '',
    measurement: '',
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (tenantId) {
      // Check permissions for loading data
      if (isEditMode && !canEdit) {
        setError('You do not have permission to edit RCA records');
        setLoading(false);
        return;
      }
      if (!isEditMode && !canCreate) {
        setError('You do not have permission to create RCA records');
        setLoading(false);
        return;
      }
      loadInitialData();
    } else {
      setError('Please login to work with RCA records');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id, isEditMode, canCreate, canEdit]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      const [plantsData, machinesData, triggersData, statusesData] =
        await Promise.all([
          plantService.getAllPlants(),
          machineService.getAllMachines(),
          masterDataService.getTriggers?.(tenantId) || Promise.resolve([]),
          masterDataService.getStatuses?.(tenantId) || Promise.resolve([]),
        ]);

      const pData = plantsData || [];
      setPlants(pData);

      // Auto-select plant if only 1 exists
      if (!isEditMode && pData.length === 1) {
        setFormData(prev => ({ ...prev, plantId: pData[0].plantId.toString() }));
      }
      setMachines(machinesData || []);
      setTriggers(triggersData || []);

      const rcaStatuses = statusesData.filter(
        (s) => !s.statusType || s.statusType === 'RCA'
      );
      setStatuses(rcaStatuses);

      if (isEditMode) {
        await loadRcaRecord();
      }
    } catch (err) {
      console.error('Error loading RCA initial data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadRcaRecord = async () => {
    try {
      const record = await rcaService.getRcaRecordById(id);
      setFormData({
        plantId: record.plantId?.toString() || '',
        machineId: record.machineId?.toString() || '',
        triggerReasonId: record.triggerReasonId?.toString() || '',
        triggerReason: record.triggerReason || '',
        problemDesc: record.problemDesc || '',
        rootCause: record.rootCause || '',
        method: record.method || '5 Whys',
        statusId: record.statusId?.toString() || '',
      });
    } catch (err) {
      console.error('Error loading RCA record:', err);
      setError('Failed to load RCA record. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFiveWhyChange = (index, value) => {
    const updated = [...fiveWhys];
    updated[index] = value;
    setFiveWhys(updated);
  };

  const handleFishboneChange = (name, value) => {
    setFishbone((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.plantId || formData.plantId === '') {
      errors.plantId = 'Plant is required';
    } else {
      const plantIdNum = parseInt(formData.plantId, 10);
      if (isNaN(plantIdNum) || plantIdNum <= 0) {
        errors.plantId = 'Please select a valid plant';
      }
    }

    if (!formData.problemDesc?.trim()) {
      errors.problemDesc = 'Problem description is required';
    }

    if (!formData.method?.trim()) {
      errors.method = 'Method is required';
    }

    if (!formData.statusId || formData.statusId === '') {
      errors.statusId = 'Status is required';
    } else {
      const statusIdNum = parseInt(formData.statusId, 10);
      if (isNaN(statusIdNum) || statusIdNum <= 0) {
        errors.statusId = 'Please select a valid status';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildRootCauseFromTools = () => {
    const sections = [];

    const filledWhys = fiveWhys.filter((w) => w && w.trim().length > 0);
    if (filledWhys.length > 0) {
      const whyLines = filledWhys
        .map((w, idx) => `Why ${idx + 1}: ${w.trim()}`)
        .join('\n');
      sections.push(`5 Whys Analysis:\n${whyLines}`);
    }

    const fishboneParts = Object.entries(fishbone)
      .filter(([, v]) => v && v.trim().length > 0)
      .map(([k, v]) => {
        const label = k.charAt(0).toUpperCase() + k.slice(1);
        return `${label}: ${v.trim()}`;
      });
    if (fishboneParts.length > 0) {
      sections.push(`Fishbone Analysis:\n${fishboneParts.join('\n')}`);
    }

    if (sections.length === 0) {
      return formData.rootCause?.trim() || '';
    }

    const baseRootCause = formData.rootCause?.trim()
      ? `Summary Root Cause:\n${formData.rootCause.trim()}\n\n`
      : '';

    return `${baseRootCause}${sections.join('\n\n')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permissions before submitting
    if (isEditMode && !canEdit) {
      setError('You do not have permission to edit RCA records');
      return;
    }
    if (!isEditMode && !canCreate) {
      setError('You do not have permission to create RCA records');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      // Ensure plantId and statusId are valid integers
      const plantIdValue = parseInt(formData.plantId, 10);
      const statusIdValue = parseInt(formData.statusId, 10);
      
      if (isNaN(plantIdValue) || plantIdValue <= 0) {
        setFormErrors(prev => ({ ...prev, plantId: 'Plant is required' }));
        setSubmitting(false);
        return;
      }
      
      if (isNaN(statusIdValue) || statusIdValue <= 0) {
        setFormErrors(prev => ({ ...prev, statusId: 'Status is required' }));
        setSubmitting(false);
        return;
      }

      // Build payload - only include scalar ID fields, NOT navigation properties
      // The backend model binding will handle the navigation properties from the IDs
      const payload = {
        plantId: plantIdValue,
        statusId: statusIdValue,
        problemDesc: formData.problemDesc?.trim() || null,
        rootCause: buildRootCauseFromTools(),
        method: formData.method?.trim() || null,
        createdBy: currentUser?.userId || 0,
      };

      // Add optional ID fields only if they have values
      if (formData.machineId) {
        const machineIdValue = parseInt(formData.machineId, 10);
        if (!isNaN(machineIdValue) && machineIdValue > 0) {
          payload.machineId = machineIdValue;
        }
      }

      if (formData.triggerReasonId) {
        const triggerIdValue = parseInt(formData.triggerReasonId, 10);
        if (!isNaN(triggerIdValue) && triggerIdValue > 0) {
          payload.triggerReasonId = triggerIdValue;
        }
      }

      // Database requires triggerReason to be NOT NULL, so always include it
      payload.triggerReason = formData.triggerReason?.trim() || '';

      // For edit mode, include rcaId
      if (isEditMode) {
        payload.rcaId = parseInt(id, 10);
      }

      // Debug: Log the payload to see what we're sending
      console.log('RCA Payload being sent:', JSON.stringify(payload, null, 2));

      if (isEditMode) {
        payload.rcaId = parseInt(id, 10);
        await rcaService.updateRcaRecord(id, payload);
        setSuccess('RCA record updated successfully!');
      } else {
        await rcaService.createRcaRecord(payload);
        setSuccess('RCA record created successfully!');
      }

      setTimeout(() => {
        navigate('/home/rca');
      }, 1200);
    } catch (err) {
      console.error('Error saving RCA record:', err);
      
      // Handle validation errors from backend
      if (err.response?.data?.errors) {
        const backendErrors = err.response.data.errors;
        const errorMessages = [];
        const fieldErrors = {};
        
        // Map backend field names to frontend field names
        Object.keys(backendErrors).forEach(key => {
          const errorArray = backendErrors[key];
          if (Array.isArray(errorArray) && errorArray.length > 0) {
            if (key === 'Plant' || key === 'plantId') {
              fieldErrors.plantId = errorArray[0];
            } else if (key === 'Status' || key === 'statusId') {
              fieldErrors.statusId = errorArray[0];
            } else {
              errorMessages.push(`${key}: ${errorArray[0]}`);
            }
          }
        });
        
        if (Object.keys(fieldErrors).length > 0) {
          setFormErrors(prev => ({ ...prev, ...fieldErrors }));
        }
        
        const msg = errorMessages.length > 0 
          ? errorMessages.join(', ')
          : err.response?.data?.message || 
            `Failed to ${isEditMode ? 'update' : 'create'} RCA record. Please check the form and try again.`;
        setError(msg);
      } else {
        const msg =
          err.response?.data?.message ||
          err.message ||
          `Failed to ${isEditMode ? 'update' : 'create'} RCA record. Please try again.`;
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="rca-entry-page pagecontainer">
        <PageLoading message="Loading RCA…" />
      </div>
    );
  }

  return (
    <div className="pagecontainer rca-entry-page">
      <div className="page-header">
        <div className="header-content">
          <h1>{isEditMode ? 'Edit RCA' : 'New Root Cause Analysis'}</h1>
          <p>
            Document the problem, run Fishbone and 5 Whys, and capture the final
            root cause.
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="rca-entry-container">
        <form onSubmit={handleSubmit} className="rca-entry-form">
          <div className="form-section">
            <h3 className="section-title">Context</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="plantId">
                  Plant <span className="required">*</span>
                </label>
                <select
                  id="plantId"
                  name="plantId"
                  value={formData.plantId}
                  onChange={handleInputChange}
                  className={formErrors.plantId ? 'error' : ''}
                >
                  <option value="">Select Plant</option>
                  {plants.map((p) => (
                    <option key={p.plantId} value={p.plantId}>
                      {p.plantName}
                    </option>
                  ))}
                </select>
                {formErrors.plantId && (
                  <span className="field-error">{formErrors.plantId}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="machineId">Machine</label>
                <select
                  id="machineId"
                  name="machineId"
                  value={formData.machineId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Machine (optional)</option>
                  {machines.map((m) => (
                    <option key={m.machineId} value={m.machineId}>
                      {m.machineName} ({m.machineCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="triggerReasonId">Trigger Reason</label>
                <select
                  id="triggerReasonId"
                  name="triggerReasonId"
                  value={formData.triggerReasonId}
                  onChange={handleInputChange}
                >
                  <option value="">Select Trigger (optional)</option>
                  {triggers.map((t) => (
                    <option key={t.triggerId} value={t.triggerId}>
                      {t.triggerName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="triggerReason">Trigger Details</label>
                <input
                  id="triggerReason"
                  name="triggerReason"
                  type="text"
                  value={formData.triggerReason}
                  onChange={handleInputChange}
                  maxLength={150}
                  placeholder="Short description of what triggered this RCA"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Problem Description</h3>
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="problemDesc">
                  Problem Statement <span className="required">*</span>
                </label>
                <textarea
                  id="problemDesc"
                  name="problemDesc"
                  value={formData.problemDesc}
                  onChange={handleInputChange}
                  rows={4}
                  className={formErrors.problemDesc ? 'error' : ''}
                  placeholder="Describe the problem in clear, measurable terms..."
                />
                {formErrors.problemDesc && (
                  <span className="field-error">{formErrors.problemDesc}</span>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">RCA Tools</h3>
            <p className="section-subtitle">
              Use 5 Whys and Fishbone to explore potential causes. The content
              will be saved together in the Root Cause field.
            </p>
            <div className="rca-tools-grid">
              <div className="tool-card">
                <div className="tool-title">5 Whys Analysis</div>
                <div className="tool-hint">
                  Ask “Why?” repeatedly until you reach the underlying cause.
                </div>
                <div className="five-whys-list">
                  {fiveWhys.map((value, index) => (
                    <div className="five-whys-item" key={index}>
                      <div className="five-whys-label">
                        Why {index + 1}
                        {index === 0 && ' (immediate cause)'}
                      </div>
                      <textarea
                        value={value}
                        onChange={(e) =>
                          handleFiveWhyChange(index, e.target.value)
                        }
                        rows={2}
                        placeholder={`Answer for Why ${index + 1}...`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="tool-card">
                <div className="tool-title">Fishbone Diagram (Cause Categories)</div>
                <div className="tool-hint">
                  Capture contributing factors across standard cause categories.
                </div>
                <div className="fishbone-categories">
                  <div className="fishbone-category">
                    <label>Man (People)</label>
                    <textarea
                      value={fishbone.man}
                      onChange={(e) =>
                        handleFishboneChange('man', e.target.value)
                      }
                      placeholder="Skills, training, staffing, human errors..."
                    />
                  </div>
                  <div className="fishbone-category">
                    <label>Machine (Equipment)</label>
                    <textarea
                      value={fishbone.machine}
                      onChange={(e) =>
                        handleFishboneChange('machine', e.target.value)
                      }
                      placeholder="Breakdowns, settings, wear & tear..."
                    />
                  </div>
                  <div className="fishbone-category">
                    <label>Method (Process)</label>
                    <textarea
                      value={fishbone.method}
                      onChange={(e) =>
                        handleFishboneChange('method', e.target.value)
                      }
                      placeholder="Procedures, work instructions, sequence..."
                    />
                  </div>
                  <div className="fishbone-category">
                    <label>Material</label>
                    <textarea
                      value={fishbone.material}
                      onChange={(e) =>
                        handleFishboneChange('material', e.target.value)
                      }
                      placeholder="Raw material, specs, handling..."
                    />
                  </div>
                  <div className="fishbone-category">
                    <label>Environment</label>
                    <textarea
                      value={fishbone.environment}
                      onChange={(e) =>
                        handleFishboneChange('environment', e.target.value)
                      }
                      placeholder="Temperature, humidity, workplace conditions..."
                    />
                  </div>
                  <div className="fishbone-category">
                    <label>Measurement</label>
                    <textarea
                      value={fishbone.measurement}
                      onChange={(e) =>
                        handleFishboneChange('measurement', e.target.value)
                      }
                      placeholder="Gauges, inspection, data accuracy..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="section-title">Root Cause & Status</h3>
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="rootCause">Root Cause Summary</label>
                <textarea
                  id="rootCause"
                  name="rootCause"
                  value={formData.rootCause}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Summarise the confirmed root cause in one or two sentences..."
                />
                <span className="field-hint">
                  Tool details above will be appended automatically when saving.
                </span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="method">
                  Primary Method Used <span className="required">*</span>
                </label>
                <select
                  id="method"
                  name="method"
                  value={formData.method}
                  onChange={handleInputChange}
                  className={formErrors.method ? 'error' : ''}
                >
                  <option value="5 Whys">5 Whys</option>
                  <option value="Fishbone">Fishbone</option>
                  <option value="5 Whys + Fishbone">5 Whys + Fishbone</option>
                </select>
                {formErrors.method && (
                  <span className="field-error">{formErrors.method}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="statusId">
                  Status <span className="required">*</span>
                </label>
                <select
                  id="statusId"
                  name="statusId"
                  value={formData.statusId}
                  onChange={handleInputChange}
                  className={formErrors.statusId ? 'error' : ''}
                >
                  <option value="">Select Status</option>
                  {statuses.map((status) => (
                    <option key={status.statusId} value={status.statusId}>
                      {status.statusName}
                    </option>
                  ))}
                </select>
                {formErrors.statusId && (
                  <span className="field-error">{formErrors.statusId}</span>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate('/home/rca')}
              disabled={submitting}
            >
              <HiOutlineXMark />
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                'Saving...'
              ) : (
                <>
                  <HiOutlineCheckCircle />
                  {isEditMode ? 'Update RCA' : 'Save RCA'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RCAEntry;


